import { useState, useEffect } from 'react';
import api from '../services/api';
import {
  LuHistory, LuSearch, LuAlertTriangle, LuCheckCircle2, LuMoon, LuPencil, LuTrash2, LuX, LuCheck, LuFileSpreadsheet, LuFileText, LuDownload,
} from 'react-icons/lu';
import {
  exportExcel, exportPDF, exportEspelhoIndividual,
  DAILY_HEADERS, WEEKLY_HEADERS, MONTHLY_HEADERS,
} from './exportUtils';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}
function fmtDate(str) {
  if (!str) return '—';
  return new Date(str + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });
}
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}
function periodDefaults(period) {
  const now = new Date(), y = now.getFullYear(), m = now.getMonth();
  if (period === 'week') {
    const day = now.getDay() || 7;
    const mon = new Date(now); mon.setDate(now.getDate() - day + 1);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { from: mon.toISOString().slice(0,10), to: sun.toISOString().slice(0,10) };
  }
  if (period === 'month') {
    return {
      from: new Date(y, m, 1).toISOString().slice(0,10),
      to:   new Date(y, m+1, 0).toISOString().slice(0,10),
    };
  }
  return { from: '', to: '' };
}

// ─── Modal de edição ──────────────────────────────────────────────────────────

function EditModal({ record, onClose, onSaved }) {
  const [f, setF] = useState({
    clock_in:     toLocalInput(record.clock_in),
    lunch_out:    toLocalInput(record.lunch_out),
    lunch_in:     toLocalInput(record.lunch_in),
    clock_out:    toLocalInput(record.clock_out),
    observations: record.observations || '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!f.clock_in) { setError('Hora de entrada obrigatória'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        clock_in:     new Date(f.clock_in).toISOString(),
        lunch_out:    f.lunch_out ? new Date(f.lunch_out).toISOString() : null,
        lunch_in:     f.lunch_in  ? new Date(f.lunch_in).toISOString()  : null,
        clock_out:    f.clock_out ? new Date(f.clock_out).toISOString() : null,
        observations: f.observations,
      };
      const { data } = await api.put(`/time-records/${record.id}`, payload);
      onSaved(data);
    } catch (e) { setError(e.response?.data?.error || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const field = (label, key, required) => (
    <div>
      <label className="form-label">{label}{required ? ' *' : ''}</label>
      <input type="datetime-local" className="form-control"
        value={f[key]} onChange={(e) => set(key, e.target.value)} />
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:'var(--surface-1)', borderRadius:12, width:'100%', maxWidth:460, boxShadow:'0 8px 32px rgba(0,0,0,0.18)', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'18px 22px 0' }}>
          <div style={{ fontWeight:700, fontSize:15, color:'var(--text-1)' }}>Editar Registro de Ponto</div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-3)' }}><LuX /></button>
        </div>
        <div style={{ padding:'14px 22px 22px', display:'flex', flexDirection:'column', gap:12 }}>
          <div style={{ fontSize:12, color:'var(--text-3)' }}>
            {record.employee?.full_name} · {fmtDate(record.work_date)}
          </div>
          {error && <div className="alert-custom danger" style={{ fontSize:13 }}>{error}</div>}
          {field('Entrada', 'clock_in', true)}
          {field('Saída para almoço', 'lunch_out')}
          {field('Retorno do almoço', 'lunch_in')}
          {field('Saída geral', 'clock_out')}
          <div>
            <label className="form-label">Observações</label>
            <input className="form-control" value={f.observations}
              onChange={(e) => set('observations', e.target.value)} placeholder="Opcional" />
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:4 }}>
            <button onClick={onClose} style={btnCancel}>Cancelar</button>
            <button onClick={handleSave} disabled={saving} style={btnSave}>
              <LuCheck /> {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Botões de exportação ─────────────────────────────────────────────────────

function ExportButtons({ records, reportData, filters, activeTab }) {
  const [loadingXlsx, setLoadingXlsx] = useState(false);
  const [loadingPdf,  setLoadingPdf]  = useState(false);

  const doExcel = async () => {
    setLoadingXlsx(true);
    try { await exportExcel(records, reportData, filters); }
    catch (e) { alert('Erro ao gerar Excel: ' + e.message); }
    finally { setLoadingXlsx(false); }
  };

  const doPDF = async () => {
    setLoadingPdf(true);
    try { await exportPDF(records, reportData, filters, activeTab); }
    catch (e) { alert('Erro ao gerar PDF: ' + e.message); }
    finally { setLoadingPdf(false); }
  };

  return (
    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
      <button onClick={doExcel} disabled={loadingXlsx} style={{
        ...btnExport, background:'var(--success)', color:'var(--on-solid)',
        opacity: loadingXlsx ? 0.7 : 1,
      }}>
        <LuFileSpreadsheet size={14} />
        {loadingXlsx ? 'Gerando…' : 'Excel'}
      </button>
      <button onClick={doPDF} disabled={loadingPdf} style={{
        ...btnExport, background:'var(--danger)', color:'var(--on-solid)',
        opacity: loadingPdf ? 0.7 : 1,
      }}>
        <LuFileText size={14} />
        {loadingPdf ? 'Gerando…' : 'PDF'}
      </button>
    </div>
  );
}

// ─── Aba: Registros Diários ───────────────────────────────────────────────────

function TabDiario({ records, onEdit, onDelete }) {
  if (records.length === 0) return <EmptyState text="Nenhum registro encontrado para o período" />;
  return (
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
        <thead>
          <tr style={{ borderBottom:'2px solid var(--border)' }}>
            {[...DAILY_HEADERS, ''].map((h) => (
              <th key={h} style={{ padding:'8px 10px', textAlign:'left', color:'var(--text-3)', fontWeight:600, whiteSpace:'nowrap', fontSize:11 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((r) => (
            <tr key={r.id} style={{ borderBottom:'1px solid var(--border)' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={(e) => e.currentTarget.style.background='transparent'}
            >
              <td style={{ padding:'9px 10px', fontWeight:700, color:'var(--text-1)', whiteSpace:'nowrap' }}>{r.employee?.full_name || '—'}</td>
              <td style={{ padding:'9px 10px', color:'var(--text-3)', fontSize:11 }}>{r.employee?.department?.name || '—'}</td>
              <td style={{ padding:'9px 10px', color:'var(--text-2)', whiteSpace:'nowrap' }}>{fmtDate(r.work_date)}</td>
              <td style={{ padding:'9px 10px', color:'var(--text-1)', fontWeight:600, whiteSpace:'nowrap' }}>{fmt(r.clock_in)}</td>
              <td style={{ padding:'9px 10px', color:'var(--warning)', whiteSpace:'nowrap' }}>{fmt(r.lunch_out)}</td>
              <td style={{ padding:'9px 10px', color:'var(--info)', whiteSpace:'nowrap' }}>{fmt(r.lunch_in)}</td>
              <td style={{ padding:'9px 10px', color:'var(--danger)', fontWeight:600, whiteSpace:'nowrap' }}>{fmt(r.clock_out)}</td>
              <td style={{ padding:'9px 10px', whiteSpace:'nowrap' }}>
                {r.incomplete
                  ? <span style={{ color:'var(--text-3)', fontStyle:'italic' }}>Em aberto</span>
                  : <strong style={{ color:'var(--text-1)' }}>{r.worked_fmt}</strong>}
              </td>
              <td style={{ padding:'9px 10px', whiteSpace:'nowrap' }}>
                {!r.incomplete && r.overtime_minutes > 0
                  ? <span style={{ color:'var(--danger)', fontWeight:700 }}>{r.overtime_fmt}</span>
                  : <span style={{ color:'var(--text-3)' }}>—</span>}
              </td>
              <td style={{ padding:'9px 10px', whiteSpace:'nowrap', color:'var(--text-2)' }}>{r.lunch_fmt || '—'}</td>
              <td style={{ padding:'9px 10px' }}>
                {r.violations?.length > 0
                  ? <span title={r.violations.join('\n')} style={{ color:'var(--danger)', cursor:'help', display:'flex', alignItems:'center', gap:3 }}>
                      <LuAlertTriangle size={12} />{r.violations.length}
                    </span>
                  : r.has_night_work
                    ? <span title="Trabalho noturno (22h–5h)" style={{ color:'var(--purple)', cursor:'help' }}><LuMoon size={13} /></span>
                    : <LuCheckCircle2 size={13} color="var(--success)" />}
              </td>
              <td style={{ padding:'9px 10px', textAlign:'right', whiteSpace:'nowrap' }}>
                <button onClick={() => onEdit(r)} title="Editar" style={iconBtn('var(--text-1)')}><LuPencil size={12} /></button>
                <button onClick={() => onDelete(r.id)} title="Remover" style={iconBtn('var(--danger)')}><LuTrash2 size={12} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Aba: Resumo Semanal ──────────────────────────────────────────────────────

function TabSemanal({ reportData }) {
  const rows = reportData.flatMap((emp) =>
    (emp.weeks || []).map((wk) => ({ ...wk, emp_name: emp.full_name, dept: emp.department }))
  );
  if (rows.length === 0) return <EmptyState text="Nenhum dado semanal disponível para o período" />;
  return (
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
        <thead>
          <tr style={{ borderBottom:'2px solid var(--border)' }}>
            {WEEKLY_HEADERS.map((h) => (
              <th key={h} style={{ padding:'8px 12px', textAlign:'left', color:'var(--text-3)', fontWeight:600, whiteSpace:'nowrap', fontSize:11 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={(e) => e.currentTarget.style.background='transparent'}
            >
              <td style={{ padding:'10px 12px', fontWeight:700, color:'var(--text-1)' }}>{row.emp_name}</td>
              <td style={{ padding:'10px 12px', color:'var(--text-3)', fontSize:11 }}>{row.dept}</td>
              <td style={{ padding:'10px 12px', color:'var(--text-2)' }}>{row.week}</td>
              <td style={{ padding:'10px 12px', textAlign:'center', color:'var(--text-2)' }}>{row.days}</td>
              <td style={{ padding:'10px 12px' }}>
                <strong style={{ color: row.worked_minutes > 2640 ? 'var(--danger)' : 'var(--text-1)' }}>{row.worked_fmt}</strong>
              </td>
              <td style={{ padding:'10px 12px' }}>
                {row.daily_overtime_minutes  > 0
                  ? <span style={{ color:'var(--danger)', fontWeight:700 }}>{row.daily_overtime_fmt}</span>
                  : <span style={{ color:'var(--text-3)' }}>—</span>}
              </td>
              <td style={{ padding:'10px 12px' }}>
                {row.weekly_overtime_minutes > 0
                  ? <span style={{ color:'var(--danger)', fontWeight:700 }}>{row.weekly_overtime_fmt}</span>
                  : <span style={{ color:'var(--text-3)' }}>—</span>}
              </td>
              <td style={{ padding:'10px 12px' }}>
                {row.total_overtime_minutes  > 0
                  ? <span style={{ background:'var(--danger-soft)', color:'var(--danger)', fontWeight:800, padding:'2px 8px', borderRadius:6 }}>{row.total_overtime_fmt}</span>
                  : <span style={{ color:'var(--text-3)' }}>—</span>}
              </td>
              <td style={{ padding:'10px 12px' }}>
                {row.violations > 0
                  ? <span style={{ color:'var(--danger)', fontWeight:700, display:'flex', alignItems:'center', gap:4 }}><LuAlertTriangle size={12} />{row.violations}</span>
                  : <LuCheckCircle2 size={14} color="var(--success)" />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Aba: Resumo Mensal ───────────────────────────────────────────────────────

function TabMensal({ reportData }) {
  if (reportData.length === 0) return <EmptyState text="Nenhum dado disponível para o período" />;
  return (
    <>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
          <thead>
            <tr style={{ borderBottom:'2px solid var(--border)' }}>
              {MONTHLY_HEADERS.map((h) => (
                <th key={h} style={{ padding:'8px 12px', textAlign:'left', color:'var(--text-3)', fontWeight:600, whiteSpace:'nowrap', fontSize:11 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reportData.map((emp) => {
              const s = emp.summary;
              return (
                <tr key={emp.id} style={{ borderBottom:'1px solid var(--border)' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={(e) => e.currentTarget.style.background='transparent'}
                >
                  <td style={{ padding:'11px 12px', fontWeight:700, color:'var(--text-1)' }}>{emp.full_name}</td>
                  <td style={{ padding:'11px 12px', color:'var(--text-3)', fontSize:11 }}>{emp.department}</td>
                  <td style={{ padding:'11px 12px', color:'var(--text-2)' }}>{emp.registration || '—'}</td>
                  <td style={{ padding:'11px 12px', textAlign:'center' }}>
                    <strong style={{ color:'var(--text-1)' }}>{s.complete_records}</strong>
                    {s.total_records !== s.complete_records && (
                      <span style={{ fontSize:10, color:'var(--warning)', marginLeft:4 }}>
                        ({s.total_records - s.complete_records} em aberto)
                      </span>
                    )}
                  </td>
                  <td style={{ padding:'11px 12px' }}><strong style={{ fontSize:13 }}>{s.total_worked_fmt}</strong></td>
                  <td style={{ padding:'11px 12px' }}>
                    {s.daily_overtime_minutes  > 0 ? <span style={{ color:'var(--danger)', fontWeight:700 }}>{s.daily_overtime_fmt}</span>  : <span style={{ color:'var(--text-3)' }}>—</span>}
                  </td>
                  <td style={{ padding:'11px 12px' }}>
                    {s.weekly_overtime_minutes > 0 ? <span style={{ color:'var(--danger)', fontWeight:700 }}>{s.weekly_overtime_fmt}</span> : <span style={{ color:'var(--text-3)' }}>—</span>}
                  </td>
                  <td style={{ padding:'11px 12px' }}>
                    {s.total_overtime_minutes  > 0
                      ? <span style={{ background:'var(--danger-soft)', border:'1px solid var(--danger-border)', color:'var(--danger)', fontWeight:800, padding:'3px 10px', borderRadius:6, fontSize:13 }}>{s.total_overtime_fmt}</span>
                      : <span style={{ color:'var(--success)', fontWeight:700 }}>—</span>}
                  </td>
                  <td style={{ padding:'11px 12px', textAlign:'center' }}>
                    {s.night_work_days > 0
                      ? <span style={{ color:'var(--purple)', fontWeight:700, display:'flex', alignItems:'center', gap:4, justifyContent:'center' }}><LuMoon size={12} />{s.night_work_days}</span>
                      : <span style={{ color:'var(--text-3)' }}>—</span>}
                  </td>
                  <td style={{ padding:'11px 12px', textAlign:'center' }}>
                    {s.total_violations > 0
                      ? <span style={{ color:'var(--danger)', fontWeight:700, display:'flex', alignItems:'center', gap:4, justifyContent:'center' }}><LuAlertTriangle size={12} />{s.total_violations}</span>
                      : <LuCheckCircle2 size={15} color="var(--success)" />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legenda CLT */}
      <div style={{ marginTop:16, padding:'12px 16px', background:'var(--surface-2)', borderRadius:8, fontSize:11.5, color:'var(--text-2)', lineHeight:1.9 }}>
        <strong style={{ color:'var(--text-1)' }}>Base legal — CLT (setor privado):</strong>{' '}
        Art. 58: 8h/dia · 44h/semana &nbsp;|&nbsp;
        Art. 59: máx. 2h extras/dia (+50% úteis, +100% dom./feriados) &nbsp;|&nbsp;
        Art. 71: intervalo 1h–2h p/ jornada &gt;6h &nbsp;|&nbsp;
        Art. 73: noturno 22h–5h (hora = 52,5 min)
      </div>
    </>
  );
}

// ─── Aba: Banco de Horas ──────────────────────────────────────────────────────

function TabBancoHoras({ reportData }) {
  if (reportData.length === 0) return <EmptyState text="Nenhum dado disponível para o período" />;
  const total = reportData.reduce((s, e) => s + (e.summary?.total_overtime_minutes || 0), 0);
  const fmtMin = (m) => {
    if (!m) return '—';
    return `${String(Math.floor(m/60)).padStart(2,'0')}h${String(m%60).padStart(2,'0')}m`;
  };
  return (
    <>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
          <thead>
            <tr style={{ borderBottom:'2px solid var(--border)' }}>
              {['Funcionário','Departamento','Matrícula','Dias Trabalhados','HE Diárias','HE Semanais','Saldo do Período'].map((h) => (
                <th key={h} style={{ padding:'8px 12px', textAlign:'left', color:'var(--text-3)', fontWeight:600, whiteSpace:'nowrap', fontSize:11 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reportData.map((emp) => {
              const s = emp.summary;
              const saldo = s.total_overtime_minutes || 0;
              return (
                <tr key={emp.id} style={{ borderBottom:'1px solid var(--border)' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={(e) => e.currentTarget.style.background='transparent'}
                >
                  <td style={{ padding:'11px 12px', fontWeight:700, color:'var(--text-1)' }}>{emp.full_name}</td>
                  <td style={{ padding:'11px 12px', color:'var(--text-3)', fontSize:11 }}>{emp.department}</td>
                  <td style={{ padding:'11px 12px', color:'var(--text-2)' }}>{emp.registration || '—'}</td>
                  <td style={{ padding:'11px 12px', textAlign:'center', color:'var(--text-2)' }}>{s.complete_records}</td>
                  <td style={{ padding:'11px 12px' }}>
                    {s.daily_overtime_minutes > 0
                      ? <span style={{ color:'var(--danger)', fontWeight:700 }}>{fmtMin(s.daily_overtime_minutes)}</span>
                      : <span style={{ color:'var(--text-3)' }}>—</span>}
                  </td>
                  <td style={{ padding:'11px 12px' }}>
                    {s.weekly_overtime_minutes > 0
                      ? <span style={{ color:'var(--danger)', fontWeight:700 }}>{fmtMin(s.weekly_overtime_minutes)}</span>
                      : <span style={{ color:'var(--text-3)' }}>—</span>}
                  </td>
                  <td style={{ padding:'11px 12px' }}>
                    {saldo > 0
                      ? <span style={{ background:'var(--danger-soft)', border:'1px solid var(--danger-border)', color:'var(--danger)', fontWeight:800, padding:'3px 12px', borderRadius:6, fontSize:13 }}>
                          +{fmtMin(saldo)}
                        </span>
                      : <span style={{ color:'var(--success)', fontWeight:700 }}>Zerado</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {total > 0 && (
        <div style={{ marginTop:14, padding:'10px 14px', background:'var(--warning-soft)', border:'1px solid var(--warning-border)', borderRadius:8, fontSize:12, color:'var(--warning)' }}>
          <strong>Saldo total de horas extras no período:</strong> {fmtMin(total)} &nbsp;|&nbsp;
          CLT art. 59 §2º — banco de horas deve ser compensado em até 6 meses (acordo individual) ou 1 ano (acordo coletivo).
        </div>
      )}
    </>
  );
}

function EmptyState({ text }) {
  return (
    <div className="empty-state" style={{ padding:'40px 0' }}>
      <LuHistory size={32} color="var(--text-3)" style={{ marginBottom:8 }} />
      <p>{text}</p>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function TimesheetPage() {
  const [departments, setDepartments] = useState([]);
  const [employees,   setEmployees]   = useState([]);
  const [filters,     setFilters]     = useState({ department_id:'', employee_id:'', date_from:'', date_to:'', period:'month' });
  const [records,     setRecords]     = useState([]);
  const [reportData,  setReportData]  = useState([]);
  const [activeTab,   setActiveTab]   = useState('diario');
  const [loading,     setLoading]     = useState(false);
  const [searched,    setSearched]    = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [error,       setError]       = useState('');

  useEffect(() => {
    api.get('/departments').then((r) => setDepartments(r.data)).catch(console.error);
    // Carrega período atual por padrão
    const { from, to } = periodDefaults('month');
    setFilters((f) => ({ ...f, date_from: from, date_to: to }));
  }, []);

  useEffect(() => {
    if (!filters.department_id) { setEmployees([]); return; }
    api.get(`/departments/${filters.department_id}/employees`).then((r) => setEmployees(r.data)).catch(console.error);
  }, [filters.department_id]);

  const setPeriod = (period) => {
    const { from, to } = periodDefaults(period);
    setFilters((f) => ({ ...f, period, date_from: from, date_to: to }));
  };
  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const handleSearch = async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (filters.department_id) params.department_id = filters.department_id;
      if (filters.employee_id)   params.employee_id   = filters.employee_id;
      if (filters.date_from)     params.date_from     = filters.date_from;
      if (filters.date_to)       params.date_to       = filters.date_to;
      const [rawRes, rptRes] = await Promise.all([
        api.get('/time-records', { params }),
        api.get('/time-records/report', { params }),
      ]);
      setRecords(rawRes.data);
      setReportData(rptRes.data);
      setSearched(true);
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao buscar dados');
    } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este registro?')) return;
    try {
      await api.delete(`/time-records/${id}`);
      await handleSearch();
    } catch (e) { setError(e.response?.data?.error || 'Erro ao remover'); }
  };

  const handleSaved = async () => { setEditing(null); await handleSearch(); };

  const fmtMin = (min) => {
    if (!min) return '—';
    return `${String(Math.floor(min/60)).padStart(2,'0')}h${String(min%60).padStart(2,'0')}m`;
  };

  const totalWorked = reportData.reduce((s, e) => s + (e.summary?.total_worked_minutes || 0), 0);
  const totalOT     = reportData.reduce((s, e) => s + (e.summary?.total_overtime_minutes || 0), 0);
  const totalAlerts = reportData.reduce((s, e) => s + (e.summary?.total_violations || 0), 0);
  const hasData     = searched && (records.length > 0 || reportData.length > 0);

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Folha de Ponto</div>
          <div className="page-card-subtitle">Relatório gerencial de jornada — CLT setor privado</div>
        </div>
        <LuHistory size={28} color="var(--accent)" />
      </div>

      {error && <div className="alert-custom danger" style={{ marginBottom:12 }}>{error}</div>}

      {/* Filtros */}
      <div style={{ background:'var(--surface-2)', border:'1px solid var(--border)', borderRadius:10, padding:'14px 16px', marginBottom:16 }}>
        <div style={{ display:'flex', gap:8, marginBottom:12 }}>
          {[['week','Esta semana'],['month','Este mês'],['custom','Personalizado']].map(([v,l]) => (
            <button key={v}
              onClick={() => v !== 'custom' ? setPeriod(v) : setFilter('period','custom')}
              style={{
                padding:'5px 13px', borderRadius:6, border:'1px solid',
                borderColor: filters.period===v ? 'var(--accent)' : 'var(--border)',
                background:  filters.period===v ? 'var(--accent-soft)' : 'var(--surface-1)',
                color:       filters.period===v ? 'var(--accent)' : 'var(--text-2)',
                cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'inherit',
              }}
            >{l}</button>
          ))}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px,1fr))', gap:10 }}>
          <div>
            <label className="form-label">Departamento</label>
            <select className="form-control" value={filters.department_id}
              onChange={(e) => { setFilter('department_id',e.target.value); setFilter('employee_id',''); }}>
              <option value="">Todos</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Funcionário</label>
            <select className="form-control" value={filters.employee_id}
              onChange={(e) => setFilter('employee_id',e.target.value)}
              disabled={!filters.department_id}>
              <option value="">Todos</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Data início</label>
            <input type="date" className="form-control" value={filters.date_from}
              onChange={(e) => { setFilter('date_from',e.target.value); setFilter('period','custom'); }} />
          </div>
          <div>
            <label className="form-label">Data fim</label>
            <input type="date" className="form-control" value={filters.date_to}
              onChange={(e) => { setFilter('date_to',e.target.value); setFilter('period','custom'); }} />
          </div>
          <div style={{ display:'flex', alignItems:'flex-end' }}>
            <button className="btn-salvar" onClick={handleSearch} disabled={loading}
              style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <LuSearch size={12} /> {loading ? 'Buscando…' : 'Buscar'}
            </button>
          </div>
        </div>
      </div>

      {/* Cards de resumo */}
      {hasData && reportData.length > 0 && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:10, marginBottom:16 }}>
          {[
            { label:'Funcionários',     value: reportData.length,      color:'var(--text-1)', bg:'var(--surface-2)' },
            { label:'Total Trabalhado', value: fmtMin(totalWorked),    color:'var(--success)', bg:'var(--success-soft)' },
            { label:'Horas Extras',     value: fmtMin(totalOT),        color: totalOT>0?'var(--danger)':'var(--success)', bg: totalOT>0?'var(--danger-soft)':'var(--success-soft)' },
            { label:'Alertas CLT',      value: totalAlerts,            color: totalAlerts>0?'var(--danger)':'var(--success)', bg: totalAlerts>0?'var(--danger-soft)':'var(--success-soft)' },
          ].map((c) => (
            <div key={c.label} style={{ background:c.bg, border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px' }}>
              <div style={{ fontSize:10, color:'var(--text-3)', fontWeight:600, textTransform:'uppercase', marginBottom:5 }}>{c.label}</div>
              <div style={{ fontSize:20, fontWeight:800, color:c.color }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Abas + exportação */}
      {searched && (
        <>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'2px solid var(--border)', marginBottom:16, flexWrap:'wrap', gap:8 }}>
            <div style={{ display:'flex', gap:2 }}>
              {[
                ['diario','Registros Diários'],
                ['semanal','Resumo Semanal'],
                ['mensal','Resumo Mensal'],
                ['banco','Banco de Horas'],
              ].map(([id,label]) => (
                <button key={id} onClick={() => setActiveTab(id)} style={{
                  padding:'9px 16px', border:'none', background:'none', cursor:'pointer',
                  fontFamily:'inherit', fontSize:13,
                  fontWeight: activeTab===id ? 700 : 400,
                  color: activeTab===id ? 'var(--text-1)' : 'var(--text-3)',
                  borderBottom: activeTab===id ? '2px solid var(--accent)' : '2px solid transparent',
                  marginBottom:-2,
                }}>{label}</button>
              ))}
            </div>
            {hasData && (
              <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
                <ExportButtons
                  records={records}
                  reportData={reportData}
                  filters={filters}
                  activeTab={activeTab}
                />
                {/* Espelho Individual — só quando um único funcionário está selecionado */}
                {filters.employee_id && reportData.length === 1 && (
                  <button onClick={async () => {
                    try { await exportEspelhoIndividual(reportData[0], records, filters); }
                    catch (e) { alert('Erro ao gerar Espelho: ' + e.message); }
                  }} style={{ ...btnExport, background:'var(--surface-3)', color:'var(--text-1)' }}>
                    <LuFileText size={14} /> Espelho
                  </button>
                )}
                {/* AFD — download do arquivo fiscal */}
                <button onClick={() => {
                  const p = new URLSearchParams();
                  if (filters.date_from)     p.set('date_from',     filters.date_from);
                  if (filters.date_to)       p.set('date_to',       filters.date_to);
                  if (filters.employee_id)   p.set('employee_id',   filters.employee_id);
                  if (filters.department_id) p.set('department_id', filters.department_id);
                  const token = localStorage.getItem('token');
                  fetch(`/api/time-records/afd?${p}`, { headers: { Authorization: `Bearer ${token}` } })
                    .then((r) => r.blob())
                    .then((blob) => {
                      const url = URL.createObjectURL(blob);
                      const a   = document.createElement('a');
                      a.href     = url;
                      a.download = `AFD-${new Date().toISOString().slice(0,10)}.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                    })
                    .catch(() => alert('Erro ao gerar AFD'));
                }} style={{ ...btnExport, background:'var(--text-3)', color:'var(--text-1)' }}>
                  <LuDownload size={14} /> AFD
                </button>
              </div>
            )}
          </div>

          {activeTab==='diario' && <TabDiario  records={records} onEdit={setEditing} onDelete={handleDelete} />}
          {activeTab==='semanal' && <TabSemanal reportData={reportData} />}
          {activeTab==='mensal'  && <TabMensal  reportData={reportData} />}
          {activeTab==='banco'   && <TabBancoHoras reportData={reportData} />}
        </>
      )}

      {!searched && !loading && <EmptyState text="Selecione o período e clique em Buscar" />}

      {editing && (
        <EditModal record={editing} onClose={() => setEditing(null)} onSaved={handleSaved} />
      )}
    </div>
  );
}

const iconBtn   = (c) => ({ background:'none', border:'none', cursor:'pointer', color:c, padding:'3px 5px', borderRadius:4 });
const btnSave   = { padding:'8px 18px', border:'none', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:700, fontFamily:'inherit', background:'var(--accent)', color:'var(--on-accent)', display:'flex', alignItems:'center', gap:6 };
const btnCancel = { padding:'8px 16px', border:'1px solid var(--border)', borderRadius:8, background:'var(--surface-1)', cursor:'pointer', fontSize:13, fontFamily:'inherit', color:'var(--text-2)' };
const btnExport = { padding:'7px 14px', border:'none', borderRadius:7, cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'inherit', display:'flex', alignItems:'center', gap:6, transition:'opacity 0.15s' };
